/* The panel that matters most: the questions the assistant is failing.
 *
 * The queue selects on a union of four signals rather than one metric, since each catches a
 * different failure — a thin corpus, a wrong answer, a user giving up, and the service
 * itself.
 */

import { useState } from 'react';
import { Button, Modal } from '@nextleap-al/admin-ui';

import { write, detailOf } from '../api/client';
import type { Gap, GapDetail as GapDetailData, GapsResponse } from '../api/types';
import { Async, PanelCard } from '../components/Panel';
import { Signal, Signals } from '../components/Signal';
import { AnswerCell, Muted, Table } from '../components/Table';
import { TileRow, type TileSpec } from '../components/Tiles';
import { useApi } from '../hooks/useApi';
import { num, plural, reasonLabel, shortDate } from '../lib/format';
import { useApp } from '../state/app';

/** Stated here rather than left to the server's default, because the tiles have to know
 *  whether the list they are counting was cut off. */
const LIMIT = 40;

/** Why one grouped question is on the list, in the words of somebody who did not build it. */
export function gapReason(gap: Gap, threshold: number): string {
  const bits: string[] = [];
  if (gap.failures) bits.push('the assistant failed to reply');
  if (gap.thumbs_down) bits.push('people marked the answer as not helpful');
  if (gap.escalations) bits.push('it ended in a support ticket');
  if (gap.avg_confidence != null && gap.avg_confidence < threshold) {
    bits.push('no document really covered it');
  }
  // Every row is here for at least one of the four, but the threshold that put it there is
  // the API's, so a row with none of the above is still explained rather than left blank.
  return bits.length ? bits.join('; ') : 'the assistant struggled with it';
}

function GapDetail({ queryNorm }: { queryNorm: string }) {
  const { word, reload } = useApp();
  const query = useApi<GapDetailData>('/api/gaps/detail', { query_norm: queryNorm });
  const [saved, setSaved] = useState<string | null>(null);

  async function resolve(status: 'documented' | 'wontfix') {
    const { ok, data } = await write('/api/gaps/resolve', { query_norm: queryNorm, status });
    setSaved(ok ? 'Saved.' : detailOf(data, 'Could not save.'));
    // The row behind this dialog carries the status, so it has to be re-asked — ticking a
    // question off and finding it still untouched underneath reads as a failed write.
    if (ok) reload();
  }

  return (
    <Async query={query}>
      {(detail) => (
        <div className="space-y-5">
          <section>
            <h3 className="mb-2 text-sm font-semibold text-[var(--text-primary)]">
              {word('Asked as', 'How people asked it')}
            </h3>
            <Table
              rows={detail.occurrences}
              // The same question asked twenty times is twenty rows here, and the two
              // sections under this one are what the reader came for.
              pageSize={8}
              columns={[
                { id: 'ts', label: 'When', render: (r) => shortDate(r.ts), sortValue: (r) => r.ts },
                { key: 'query', label: 'Question' },
                { id: 'answer', label: 'What it answered', render: (r) => <AnswerCell text={r.answer} /> },
                { key: 'user_role', label: 'Role' },
                {
                  id: 'confidence',
                  label: 'Evidence',
                  num: true,
                  advanced: true,
                  render: (r) => num(r.confidence, 2),
                  sortValue: (r) => r.confidence,
                },
              ]}
            />
          </section>

          <section>
            <h3 className="mb-2 text-sm font-semibold text-[var(--text-primary)]">
              {word('Documents retrieved instead', 'Documents it used instead')}
            </h3>
            <Table
              rows={detail.retrieved}
              empty="Nothing was retrieved for this question."
              columns={[
                { key: 'source', label: 'Source', simple: 'Document' },
                { key: 'times', label: 'Times', num: true },
                {
                  id: 'avg_score',
                  label: 'Avg score',
                  num: true,
                  advanced: true,
                  render: (r) => num(r.avg_score, 2),
                  sortValue: (r) => r.avg_score,
                },
              ]}
            />
          </section>

          {detail.comments.length > 0 && (
            <section>
              <h3 className="mb-2 text-sm font-semibold text-[var(--text-primary)]">
                What readers said about it
              </h3>
              <Table
                rows={detail.comments}
                columns={[
                  { id: 'ts', label: 'When', render: (r) => shortDate(r.ts), sortValue: (r) => r.ts },
                  { id: 'reason', label: 'Reason', render: (r) => reasonLabel(r.reason) },
                  { key: 'comment', label: 'Comment' },
                ]}
              />
            </section>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => resolve('documented')}>
              {word('Mark as documented', 'I have written a document for this')}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => resolve('wontfix')}>
              Not worth documenting
            </Button>
            {saved && <span className="text-sm text-[var(--text-muted)]">{saved}</span>}
          </div>
        </div>
      )}
    </Async>
  );
}

/**
 * The four numbers this page is about: the size of the queue and the three signals that put
 * things in it. Every one is a count over the list below, not over the whole site.
 */
function tilesFor(data: GapsResponse, word: (full: string, plain: string) => string): TileSpec[] {
  const open = data.items.filter((g) => !g.status);
  const done = data.items.length - open.length;
  const sum = (pick: (gap: Gap) => number) =>
    data.items.reduce((total, gap) => total + Number(pick(gap) || 0), 0);

  return [
    {
      label: word('Open gaps', 'Waiting for a document'),
      value: num(open.length),
      // The queue is capped by the request, so a full page says so rather than reporting
      // the cap as if it were the count.
      hint:
        `of ${plural(data.items.length, 'question')} here` +
        `${data.items.length >= LIMIT ? ` — the worst ${LIMIT}` : ''}` +
        `${done ? `; ${num(done)} already ticked off` : ''}`,
    },
    {
      label: word('Occurrences', 'Times they were asked'),
      value: num(sum((gap) => gap.occurrences)),
      hint: 'the same question in different words counts once in the list below',
    },
    {
      label: word('Rated unhelpful', 'Marked as not helpful'),
      value: num(sum((gap) => gap.thumbs_down)),
      hint: 'answers somebody gave a thumbs-down',
    },
    {
      label: word('Escalated', 'Ended with a person'),
      value: num(sum((gap) => gap.escalations)),
      hint: 'the person gave up and asked for a support ticket',
    },
  ];
}

/**
 * Why this row is in the queue, as badges, showing only the signals that fired. The evidence
 * score itself is not shown — what it is evidence *of* is the part that translates.
 */
function Why({ gap, threshold, simple }: { gap: Gap; threshold: number; simple: boolean }) {
  const weak = gap.avg_confidence != null && gap.avg_confidence < threshold;

  return (
    <Signals>
      {gap.thumbs_down > 0 && <Signal tone="critical">✕ {num(gap.thumbs_down)} unhelpful</Signal>}
      {gap.escalations > 0 && <Signal tone="warning">▲ {num(gap.escalations)} escalated</Signal>}
      {gap.failures > 0 && <Signal tone="critical">! {num(gap.failures)} failed</Signal>}
      {weak && (
        <Signal>{simple ? 'no document' : `evidence ${num(gap.avg_confidence, 2)}`}</Signal>
      )}
      {!weak && !gap.thumbs_down && !gap.escalations && !gap.failures && <Muted>—</Muted>}
    </Signals>
  );
}

export function GapsPanel() {
  const { days, system, simple, word, systemLabel } = useApp();
  const query = useApi<GapsResponse>('/api/gaps', { days, system, limit: LIMIT });
  // The row the dialog is showing. Held as the gap itself rather than as its id, so the
  // dialog keeps its heading while the list behind it is being re-fetched.
  const [open, setOpen] = useState<Gap | null>(null);

  return (
    <>
      {/* The whole page waits together, as every other panel does. Rendering the card first
          and the tiles only once the answer landed put a heading and four grey lines on
          screen for a second, then pushed them down the page when the row of numbers
          appeared above them — which reads as two pages rather than as one loading. */}
      <Async query={query} shape="page">
        {(data) => (
          <div className="space-y-5">
            <TileRow tiles={tilesFor(data, word)} />

            <PanelCard
              title="What the assistant is failing to answer"
              simpleTitle="Questions that did not get a good answer"
              hint="Weak evidence, rated unhelpful, escalated or failed — grouped, worst first. The writing queue."
              simpleHint="No document covered it, the answer was marked unhelpful, or it ended with a person. Each row is a document waiting to be written."
            >
              <Table
                rows={data.items}
                pageSize={10}
                search="Search questions"
                empty="No failing questions in this window — or question logging has not collected any yet."
                columns={[
                  {
                    id: 'example',
                    label: 'Question',
                    sortValue: (gap) => gap.example,
                    render: (gap) => (
                      <button
                        type="button"
                        onClick={() => setOpen(gap)}
                        className="text-left font-medium text-[var(--text-primary)] hover:underline"
                      >
                        {gap.example}
                      </button>
                    ),
                  },
                  {
                    id: 'why',
                    label: 'Why it is here',
                    render: (gap) => <Why gap={gap} threshold={data.threshold} simple={simple} />,
                  },
                  { key: 'occurrences', label: 'Asked', simple: 'Times asked', num: true },
                  {
                    id: 'last_seen',
                    label: 'Last asked',
                    render: (gap) => shortDate(gap.last_seen),
                    sortValue: (gap) => gap.last_seen,
                  },
                  {
                    id: 'roles',
                    label: 'Role',
                    advanced: true,
                    render: (gap) => gap.roles || <Muted>unknown</Muted>,
                    sortValue: (gap) => gap.roles,
                  },
                  {
                    id: 'systems',
                    label: 'Where',
                    advanced: true,
                    // The server joins the keys it stored; the wording is this side's job.
                    render: (gap) =>
                      gap.systems
                        ? gap.systems.split(/,\s*/).map(systemLabel).join(', ')
                        : <Muted>—</Muted>,
                    sortValue: (gap) => gap.systems,
                  },
                  {
                    id: 'status',
                    label: 'Status',
                    sortValue: (gap) => gap.status,
                    render: (gap) =>
                      gap.status ? (
                        <Signal tone="good">✓ {gap.status}</Signal>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => setOpen(gap)}>
                          {word('Open', 'Look at it')}
                        </Button>
                      ),
                  },
                ]}
              />
            </PanelCard>
          </div>
        )}
      </Async>

      {/* A dialog rather than an expanding row: opening the fifth of forty questions used to
          push the other thirty-five down the page, and closing it lost your place. */}
      <Modal
        isOpen={open !== null}
        onClose={() => setOpen(null)}
        title={open?.example ?? ''}
        description={word(
          'Every wording, what was retrieved instead, and what readers said',
          'How people asked it, what the assistant replied, and what you can do about it',
        )}
        size="xl"
      >
        {/* The dialog's own scroll. A question asked forty times lists forty wordings, and
            without a bound here the panel grows past the viewport and the whole overlay
            scrolls — taking the heading and the close button off the top of the screen.
            `overscroll-contain` stops the page behind from scrolling on at the end. */}
        <div className="max-h-[70vh] overflow-y-auto overscroll-contain pr-1">
          {open && <GapDetail queryNorm={open.query_norm} />}
        </div>
      </Modal>
    </>
  );
}
